<template>
  <div class="add-booking-wrapper">
    <transition name="fade-slide">
      <div v-if="isLoading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>
    <!-- ===== ROW 1: PAGE HEADER ===== -->
    <div class="page-header">
      <div class="header-left">
        <h2>Create Your Booking Page</h2>
        <p>Build, customize, and launch your AI-powered booking page in a few guided steps.</p>
      </div>
      <div class="header-right">
        <button class="btn-help"><i class="bx bx-help-circle"></i> Help</button>
      </div>
    </div>

    <!-- ===== ROW 2: MAIN BODY (TABS + CONTENT) ===== -->
    <div class="page-body">
      <!-- LEFT SIDE: STEP NAVIGATION -->
      <div class="step-nav">
        <div
          v-for="(s, i) in steps"
          :key="i"
          :class="['step-item', step === i ? 'active' : '']"
          @click="set_step(i)"
        >
          <i :class="s.icon"></i>
          <div class="step-info">
            <h4>{{ s.title }}</h4>
            <p>{{ s.desc }}</p>
          </div>
        </div>
      </div>

      <!-- RIGHT SIDE: STEP CONTENT -->
      <transition name="slide-fade" mode="out-in">
        <div :key="step" class="step-body">
          <!-- STEP 1 -->
          <div v-if="step === 0" class="fade-in">
            <h3><i class="bx bx-book-open"></i> Booking Page Basics</h3>
            <p class="step-desc">Let’s start by naming your booking page.</p>

            <div class="form-group">
              <label>Page Title</label>
              <input v-model="form.title" placeholder="Enter booking page title" />
            </div>

            <div class="form-group">
              <label>Custom URL</label>
              <div class="url-box">
                <span>{{ baseUrl }}</span>
                <input v-model="form.url" placeholder="your-link" />
              </div>
            </div>

            <!-- <div class="form-group">
              <label>Intro Message</label>
              <textarea v-model="form.intro" rows="3" placeholder="Brief intro for visitors"></textarea>
            </div> -->
            <label class="checkbox">
              <input type="checkbox" v-model="form.recurring" />
              <span>Enable recurring bookings</span>
            </label>
          </div>

          <!-- STEP 2 -->
          <div v-if="step === 1" class="fade-in">
            <h3><i class="bx bx-credit-card"></i> Pricing & Branding</h3>
            <p class="step-desc">Design your pricing layout, brand color, and plan structure to match your business identity.</p>

            <div class="branding-top">
              <!-- <div class="upload-circle" @click="triggerUpload">
                <input type="file" ref="fileInput" @change="handleFile" style="display: none" />
                <template v-if="!form.image">
                  <i class="bx bx-image-add"></i>
                  <span>Upload Logo</span>
                </template>
                <img v-else :src="form.image" alt="Uploaded Logo" />
              </div> -->

              <div class="form-group half">
                <label>Primary Brand Color</label>
                <div class="color-card animate-fadein">
                  <div class="color-header">
                    <div class="color-preview" :style="{ backgroundColor: form.color }">
                      <input type="color" v-model="form.color" class="color-input" />
                      <i class="bx bx-droplet"></i>
                    </div>
                    <div class="color-meta" style="margin-left: 1rem;">
                      <h5>{{ colorName }}</h5>
                      <span>{{ form.color.toUpperCase() }}</span>
                    </div>
                  </div>

                  <div class="color-palette">
                    <div
                      v-for="(color, i) in presetColors"
                      :key="i"
                      class="color-swatch"
                      :style="{ backgroundColor: color }"
                      @click="form.color = color"
                      :title="color"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <h4 class="section-title">Choose Booking Layout Style</h4>
            <p class="step-desc">Select how your booking page will appear to your visitors.</p>

            <div class="booking-layout-grid">
              <div
                v-for="layout in bookingLayouts"
                :key="layout.type"
                class="booking-layout-card"
                :class="{ active: form.layout === layout.type }"
                @click="form.layout = layout.type"
              >
                <div class="layout-illustration">
                  <div v-if="layout.type === 'Month'" class="month-view">
                    <div v-for="n in 12" :key="n" class="calendar-dot"></div>
                  </div>

                  <div v-else-if="layout.type === 'Week'" class="week-view">
                    <div v-for="n in 5" :key="n" class="week-line"></div>
                  </div>

                  <div v-else class="day-view">
                    <div class="day-block long"></div>
                    <div class="day-block short"></div>
                    <div class="day-block long"></div>
                  </div>
                </div>

                <div class="layout-footer">
                  <input type="radio" :id="layout.type" :value="layout.type" v-model="form.layout" />
                  <label :for="layout.type">{{ layout.label }}</label>
                </div>
              </div>
            </div>
           <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
    
    <div>
        <h4 class="section-title">Choose a Package</h4>
        <p class="step-desc">
            Select the package your clients can book.
        </p>
    </div>

    <!-- ⭐ Right Side Add Package Button -->
    <button 
        @click="new_package()"
        style="
            padding: 8px 14px;
            background: #111827;
            color: #fff;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
        "
    >
        <i class='bx bx-plus' style="font-size:16px;"></i>
        Add Package
    </button>

</div>

            <div class="packages-grid">
              <div
                v-for="(pkg, i) in packages"
                :key="i"
                class="package-card"
                :class="{ active: check_pkg(pkg.package_id) }"
                @click="set_pkg(pkg.package_id)"
              >
                <div class="package-header">
                  <h4>{{ pkg.package_name }}</h4>
                  <span class="price">${{ pkg.package_amount }}</span>
                </div>

                <div class="package-body">
                  <p>{{ pkg.package_desc }}</p>
                </div>

                <div class="package-footer">
                  <input 
                    type="checkbox"
                    :value="pkg.package_id"
                    v-model="form.package"
                    @click.stop 
                  />
                  <label>Select</label>
                </div>
              </div>
            </div>

          </div>

          <!-- STEP 3 -->
          <div v-if="step === 2" class="fade-in">
            <h3><i class="bx bx-calendar"></i> Availability</h3>
            <p class="step-desc">Define how long your meetings last and when you’re available.</p>

            <div class="availability-tabs">
              <div class="tab-card">
                <h4><i class="bx bx-time"></i> Meeting Duration</h4>
                <div class="option-grid">
                  <button
                    v-for="t in ['15 mins', '30 mins', '45 mins', '1 hour']"
                    :key="t"
                    :class="['option-btn', { active: form.duration === t }]"
                    @click="form.duration = t"
                  >
                    {{ t }}
                  </button>
                </div>
              </div>
              <!-- Available Days -->
              <div class="tab-card">
                <h4><i class="bx bx-calendar-check"></i> Available Days</h4>

                <div class="days-grid">
                  <button
                    v-for="day in weekDays"
                    :key="day"
                    class="day-btn"
                    :class="{ active: form.days.includes(day) }"
                    @click="toggleDay(day)"
                  >
                    {{ day }}
                  </button>
                </div>
              </div>


              <div class="tab-card">
                <h4><i class="bx bx-calendar-event"></i> Available Hours</h4>
                <div class="option-grid">
                  <button
                    v-for="h in ['9 AM - 5 PM', '10 AM - 6 PM', 'Custom']"
                    :key="h"
                    :class="['option-btn', { active: hoursType === h }]"
                    @click="hoursType = h"

                  >
                    {{ h }}
                  </button>
                </div>
                <transition name="fade-smooth" v-if="hoursType === 'Custom'">
                  <div class="custom-multi-hours">

                    <div
                      class="range-row"
                      v-for="(r,i) in customRanges"
                      :key="i"
                    >
                      <input type="time" v-model="r.from">
                      <span>to</span>
                      <input type="time" v-model="r.to">

                      <button
                        v-if="customRanges.length > 1"
                        @click="removeRange(i)"
                        class="remove-btn"
                      >✕</button>
                    </div>

                    <button class="add-btn" @click="addRange">
                      + Add Time Range
                    </button>

                  </div>
                </transition>

              </div>

              <div class="tab-card">
                <h4><i class="bx bx-timer"></i> Buffer Time</h4>
                <p>Set a gap between consecutive appointments (in minutes).</p>
                <input type="number" v-model="form.buffer" min="0" class="buffer-input" />
              </div>
            </div>
          </div>

          <!-- STEP 4 -->
          <div v-if="step === 3" class="fade-in">
            <h3><i class="bx bx-calendar"></i> Calendar Integrations</h3>
            <p class="step-desc">Sync your bookings automatically with your preferred calendar service.</p>

            <div class="calendar-grid">
              <div v-for="(g, i) in calendars" :key="i" class="calendar-card">
                <div class="calendar-left">
                  <div class="calendar-logo">
                    <i :class="g.logo"></i>
                  </div>
                  <div class="calendar-info">
                    <h4>{{ g.name }}</h4>
                    <p>{{ g.desc }}</p>
                  </div>
                </div>
                <div class="calendar-right">
                  
                    <!-- :disabled="g.status === 'Connected' || g.upcoming" -->
                  <button
                    class="btn-connect"
                    :disabled="g.status === 'Connected' || g.upcoming"
                    :class="{ connected: g.status === 'Connected', upcoming: g.upcoming }"
                    @click="connectCalendar(g)"
                  >
                    {{ g.status === 'Connected' ? 'Connected' : g.upcoming ? 'Coming Soon' : 'Connect' }}
                  </button>
                  <span class="status" :class="{ connected: g.status === 'Connected', notConnected: g.status === 'Not Connected', upcoming: g.upcoming }">
                    {{ g.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- STEP 5 -->
          <div v-if="step === 4" class="fade-in">
            <h3><i class="bx bx-credit-card"></i> Payment Gateways</h3>
            <p class="step-desc">Connect secure gateways for accepting payments.</p>

            <div class="payment-grid">
              <div v-for="(g, i) in gateways" :key="i" class="payment-card">
                <div class="gateway-left">
                  <div class="gateway-logo" :style="{ background: g.color }">
                    <i :class="g.logo"></i>
                  </div>
                  <div class="gateway-info">
                    <h4>{{ g.name }}</h4>
                    <p>{{ g.desc }}</p>
                  </div>
                </div>
                <div class="gateway-right">
                  <button class="btn-black"
                  @click="g.name === 'Stripe' ? connectStripe(gateway) :
                        g.name === 'PayPal' ? connectPayPal(gateway) : ''"
                  >Connect</button>
                  <span
                    class="status"
                    :style="{
                      color: g.status === 'Connected' ? '#10B981' : '#EF4444',
                      fontWeight: '600',
                    }"
                    >{{ g.status }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- STEP 6 -->
          <div v-if="step === 5" class="fade-in">
            <h3><i class="bx bx-spreadsheet"></i> Routing Forms</h3>
            <p class="step-desc">Collect the right information before someone books on your calendar.</p>

            <div class="form-layout-header">
              <h4>Your Form Layout</h4>
              <div style="display: flex;  gap: 1rem;">
                <button class="btn-add" @click="gotoformadd()"><i class="bx bx-plus"></i> Add</button>
                <button class="btn-add" style="background-color: #000;" @click="resetform()"><i class="bx bx-refresh"></i> Reset</button>
              </div>
            </div>

            <div class="routing-grid">
              <div
                v-for="(form, i) in routingForms"
                :key="i"
                :class="['form-card', { active: selectedForm === form.form_temp_id }]"
                @click="selectedForm = form.form_temp_id"
              >
                <div class="form-preview">
                  <div class="form-top">
                    <div class="circle"></div>
                    <div class="lines">
                      <div class="line short"></div>
                      <div class="line long"></div>
                    </div>
                  </div>
                  <div class="form-body">
                    <div class="dot-grid">
                      <div v-for="n in 16" :key="n" class="dot"></div>
                    </div>
                  </div>
                </div>
                <div class="form-footer">
                  <input type="radio" name="formSelect" :checked="selectedForm === form.form_temp_id" />
                  <label>{{ form.form_temp_title }}</label>
                </div>
              </div>
            </div>
          </div>

          <!-- STEP 7 -->
          <div v-if="step === 6" class="fade-in">
            <h3><i class="bx bx-bell"></i> Reminders for Meeting</h3>
            <p class="step-desc">
              Stay organized and never miss important tasks with timely reminders and notifications.
            </p>
            <div class="reminder-options">
              <label v-for="(r, i) in reminderOptions" :key="i" class="reminder-checkbox">
                <input type="checkbox" v-model="selectedReminders" :value="r.label" />
                <span>{{ r.label }}</span>
              </label>
            </div>
          </div>

          <!-- STEP 8 -->
          <div v-if="step === 7" class="fade-in">
            <h3><i class="bx bx-map-pin"></i> Meeting Location</h3>
            <p class="step-desc">
              Choose how and where your meetings will take place — online or in-person.
            </p>

            <div class="location-grid">
              <div
                v-for="(loc, i) in meetingLocations"
                :key="i"
                :class="['location-card', { active: form.location === loc.type }]"
                @click="form.location = loc.type"
              >
                <div class="location-icon" :style="{ backgroundColor: loc.color }">
                  <i :class="loc.icon"></i>
                </div>
                <div class="location-details">
                  <h4>{{ loc.name }}</h4>
                  <p>{{ loc.desc }}</p>
                </div>
                <div class="location-select">
                  <input type="radio" name="location" :value="loc.type" v-model="form.location" />
                </div>
              </div>
            </div>

            <transition name="fade-smooth">
              <div v-if="form.location === 'custom'" class="custom-link-box">
                <label>Enter Custom Meeting Link</label>
                <input type="url" v-model="form.customLink" placeholder="https://yourmeetinglink.com/session" />
              </div>
            </transition>
          </div>

          <!-- STEP 9 -->
          <div v-if="step === 8" class="fade-in">
            <h3><i class="bx bx-rocket"></i> Review & Launch</h3>
            <p class="step-desc">Double-check your setup before going live.</p>

            <div class="review-card">
              <div class="review-section">
                <h4>📄 Page Info</h4>
                <ul>
                  <li><strong>Title:</strong> {{ form.title || '—' }}</li>
                  <li><strong>URL:</strong> {{ baseUrl + (form.url || '') }}</li>
                  <!-- <li><strong>Intro:</strong> {{ form.intro || '—' }}</li> -->
                  <li><strong>Recurring:</strong> {{ form.recurring ? 'Enabled' : 'Disabled' }}</li>
                </ul>
              </div>

              <div class="review-section">
                <h4>🎨 Branding & Layout</h4>
                <ul>
                  <li><strong>Brand Color:</strong> <span :style="{ color: form.color }">{{ form.color }}</span></li>
                  <li><strong>Layout Style:</strong> {{ form.layout || '—' }}</li>
                </ul>
              </div>

              <div class="review-section">
                <h4>🕒 Availability</h4>
                <ul>
                  <li><strong>Duration:</strong> {{ form.duration || '—' }}</li>
                  <li><strong>Hours:</strong> {{ form.hours || '—' }}</li>
                  <li><strong>Buffer Time:</strong> {{ form.buffer || 0 }} min</li>
                </ul>
              </div>

              <div class="review-section">
                <h4>📅 Calendar Integrations</h4>
                <ul>
                  <li v-for="(cal, i) in calendars" :key="i">
                    <strong>{{ cal.name }}:</strong> {{ cal.status }}
                  </li>
                </ul>
              </div>

              <div class="review-section">
                <h4>💳 Payment Gateways</h4>
                <ul>
                  <li v-for="g in gateways" :key="g.name">
                    <strong>{{ g.name }}:</strong> {{ g.status }}
                  </li>
                </ul>
              </div>

              <div class="review-section">
                <h4>📋 Routing Form</h4>
                <ul>
                  <li><strong>Selected Form:</strong> {{ selectedForm || '—' }}</li>
                </ul>
              </div>

              <div class="review-section">
                <h4>🔔 Reminders</h4>
                <ul>
                  <li v-if="selectedReminders.length"><strong>Enabled:</strong> {{ selectedReminders.join(', ') }}</li>
                  <li v-else>— No reminders selected</li>
                </ul>
              </div>

              <div class="review-section">
                <h4>📍 Meeting Location</h4>
                <ul>
                  <li><strong>Type:</strong> {{ form.location || '—' }}</li>
                  <li v-if="form.customLink"><strong>Custom Link:</strong> {{ form.customLink }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- FOOTER NAV -->
    <div class="form-footer">
      <button class="btn-secondary" @click="prevStep" :disabled="step === 0">Back</button>
      <button class="btn-primary" @click="nextStep">
        {{ isSubmitting ? "⏳ Creating..." : step === steps.length - 1 ? "🚀 Create Booking Page" : "Next Step" }}
      </button>
    </div>

    <!-- ✅ Toast Notification -->
    <transition name="fade-smooth">
      <div v-if="toast.show" class="toast" :class="toast.type">
        {{ toast.message }}
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, watch,onMounted, nextTick } from "vue";
import { createBookPage,checkBookingPageUrl } from "@/apis/bookpageapis"; 
import { checkIntegration } from "@/apis/adminapis"; 
import { getPackages } from "@/apis/pkgapis";
import { getForms } from "@/apis/formapis";
import { useRouter,useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import axios from "axios";
import { apiBaseUrl,mainBaseUrl } from "@/apis/appconst";


const toast1 = useToast();
// === Router ===
const router = useRouter();

const route = useRoute();
const hoursType = ref("");

const isLoading = ref(false);
const customRanges = ref([
  { from: "", to: "" }
]);


const set_step = (i) =>{
  if(form.value.id.length>0){
    step.value = i;
  }
}

const openPopup = (url, name = "AuthPopup") => {
  const w = 600;
  const h = 700;
  const left = (window.innerWidth - w) / 2;
  const top = (window.innerHeight - h) / 2;

  return window.open(
    url,
    name,
    `width=${w},height=${h},top=${top},left=${left},resizable=yes,scrollbars=yes`
  );
};
const listenForIntegration = (type, popup, obj = null) => {
  const intv = setInterval(async () => {
    if (!popup || popup.closed) {
      clearInterval(intv);

      await refreshIntegrationStatus(type);

      if (obj) obj.status = "Connected";

      // toast1.success(`${type} connected successfully!`);
    }
  }, 1000);
};

const refreshIntegrationStatus = async (type) => {
  const userData = JSON.parse(sessionStorage.getItem("ldata"));
  const userId = userData?.user_id || "";

  let res;

  // GOOGLE CALENDAR
  if (type === "Google") {
    res = await checkIntegration(userId, "Google Calendar", "google_calendar");
    calendars[0].status = res.status ? "Connected" : "Not Connected";
  }

  // PAYPAL
  if (type === "PayPal") {
    res = await checkIntegration(userId, "PayPal", "payment");
    gateways[1].status = res.status ? "Connected" : "Not Connected";
  }

  // STRIPE
  if (type === "Stripe") {
    res = await checkIntegration(userId, "Stripe", "payment");
    gateways[0].status = res.status ? "Connected" : "Not Connected";
  }

  // RAZORPAY
  if (type === "Razorpay") {
    res = await checkIntegration(userId, "Razorpay", "payment");
    gateways[2].status = res.status ? "Connected" : "Not Connected";
  }
};

const addRange = () => {
  customRanges.value.push({ from:"", to:"" });
};

const removeRange = (i) => {
  customRanges.value.splice(i,1);
};




const check_pkg = (pkg_id)=>{
  for(var x=0;x<form.value.package.length;x++){
    if(form.value.package[x]===pkg_id){
      return true;
    }
  }
  return false;
}

const set_pkg = (id) => {
  const index = form.value.package.indexOf(id);
  if (index === -1) {
    form.value.package.push(id);
  } else {
    form.value.package.splice(index, 1);
  }
};

// === State: Stepper ===
const step = ref(0);
const isSubmitting = ref(false);
const isCalendrConnected = ref(false);
const calDt = ref(null);

const baseUrl = ref("appointment/");

// === Step Navigation ===
const steps = [
  { title: "Basics", icon: "bx bx-book-open", desc: "Page title, intro, and link." },
  { title: "Pricing", icon: "bx bx-palette", desc: "Design pricing and brand colors." },
  { title: "Availability", icon: "bx bx-time-five", desc: "Set working hours and duration." },
  { title: "Connect Calendar", icon: "bx bx-calendar-event", desc: "Connect your calendar." },
  { title: "Payments", icon: "bx bx-wallet", desc: "Connect payment gateways." },
  { title: "Routing Forms", icon: "bx bx-spreadsheet", desc: "Select your form layout." },
  { title: "Reminders", icon: "bx bx-bell", desc: "Setup automated meeting reminders." },
  { title: "Meeting Location", icon: "bx bx-map-pin", desc: "Select meeting location type." },
  // { title: "Review", icon: "bx bx-rocket", desc: "Confirm and launch your page." },
];

// === Booking Layout Types ===
const bookingLayouts = ref([
  { type: "Month", label: "Month" },
  { type: "Week", label: "Week" },
  { type: "Day", label: "Day" },
]);

// === Packages ===
const packages = ref([]);

// === Meeting Locations ===
const meetingLocations = [
  {
    type: "google",
    name: "Google Meet",
    desc: "Instant online video meeting powered by Google.",
    icon: "bx bxl-google",
    color: "#34A853",
    status:true
  },
  {
    type: "inperson",
    name: "In-Person Meeting",
    desc: "Meet clients or team at a physical office location.",
    icon: "bx bx-buildings",
    color: "#6B7280",
    status:true
  },
  {
    type: "phone",
    name: "Phone Call",
    desc: "Discuss via a scheduled phone call with your clients.",
    icon: "bx bx-phone-call",
    color: "#047857",
    status:true
  },
  {
    type: "custom",
    name: "Custom Link",
    desc: "Provide your own external meeting link or platform.",
    icon: "bx bx-link-alt",
    color: "#9333EA",
    status:false
  },
];

// === Reminders ===
const reminderOptions = [
  { label: "Pre-Meeting Reminders" },
  { label: "Last-Minute & Just-in-Time Reminders" },
  { label: "Post-Meeting Reminders" },
  { label: "Reschedule or Cancellation Reminders" },
  { label: "Task & Preparation Reminders" },
];
const selectedReminders = ref([]);

// === Routing Forms ===
const routingForms = ref([]);
const selectedForm = ref("");

// === Calendars ===
const calendars = [
  {
    name: "Google Calendar",
    desc: "Sync bookings automatically with your Google account.",
    logo: "bx bxl-google",
    color: "#4285F4",
    status: "Not Connected",
    upcoming: false,
  },
  {
    name: "Outlook Calendar",
    desc: "Integration with Microsoft Outlook (coming soon).",
    logo: "bx bxl-microsoft",
    color: "#0078D4",
    status: "Upcoming",
    upcoming: true,
  },
  {
    name: "Apple Calendar",
    desc: "Connect with iCloud calendar for seamless scheduling.",
    logo: "bx bxl-apple",
    color: "#111827",
    status: "Upcoming",
    upcoming: true,
  },
];
const connectCalendar = (calendar) => {
  if (calendar.upcoming) return;

  if (calendar.name === "Google Calendar") {
    connectGoogleCalendar(calendar);
  }
};

const connectGoogleCalendar = async (obj) => {
  console.log("sdasdf",obj);
  const userData = JSON.parse(sessionStorage.getItem("ldata"));
  const userId = userData?.user_id || "";
  isLoading.value = true;
  try {
    var pid = "";
    if(sessionStorage.getItem("booking_page_data") && 
        sessionStorage.getItem("booking_page_data").length>0){

      pid = btoa(sessionStorage.getItem("booking_page_data"));
    }
    const res = await axios.post(apiBaseUrl + "google/calendar/connect", 
      { user_id:userId,rtype:"add_booking_page?pid="+pid }
    );
    isLoading.value = false;
    if (!res.data.status) {
      toast1.error(res.data.success);
      return;
    }
    sessionStorage.removeItem("ldata");
     window.location.href = res.data.url;
  } catch (e) {
    isLoading.value = false;
    toast1.error("Google Calendar connection failed");
  }
};
const connectStripe = async (obj) => {
  const userId = JSON.parse(sessionStorage.getItem("ldata"))?.user_id;
  isLoading.value = true;
  try {
    const res = await axios.post(apiBaseUrl + "stripe1/connect", {
      user_id: userId,
    });
    isLoading.value = false;
    const popup = openPopup(res.data.url);
    listenForIntegration("Stripe", popup, obj);
  } catch (e) {
    isLoading.value = false;
    toast1.error("Stripe connection failed");
  }
};

const connectPayPal = async (obj) => {
  const userId = JSON.parse(sessionStorage.getItem("ldata"))?.user_id;
  isLoading.value = true;
  try {
    const res = await axios.post(apiBaseUrl + "paypal1/connect", {
      user_id: userId,
    });
    isLoading.value = false;
    const popup = openPopup(res.data.url);
    listenForIntegration("PayPal", popup, obj);
  } catch (e) {
    isLoading.value = false;
    toast1.error("PayPal connection failed");
  }
};



const weekDays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];

// === Form Data ===
const form = ref({
  id:"",
  title: "",
  url: "",
  intro: "",
  recurring: false,
  image: "",
  color: "#111827",
  layout: "Month",
  name: "Starter Plan",
  type: "Subscription",
  duration: "30 mins",
  hours: "",
  customFrom: "",
  customTo: "",
  buffer: 0,
  package: [],
  location: "",
  customLink: "",
  days: ["Monday","Tuesday","Wednesday","Thursday","Friday"],

});

const toggleDay = (day) => {
  const i = form.value.days.indexOf(day);
  if (i === -1) {
    form.value.days.push(day);
  } else {
    form.value.days.splice(i, 1);
  }
};


// === Color Palette ===
const colorName = ref("Primary Dark");
const presetColors = ref([
  "#111827", "#1F2937", "#2563EB", "#10B981", "#F59E0B",
  "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6",
]);

watch(() => form.value.color, (newColor) => {
  const names = {
    "#111827": "Primary Dark",
    "#1F2937": "Slate Gray",
    "#2563EB": "Ocean Blue",
    "#10B981": "Emerald Green",
    "#F59E0B": "Amber Gold",
    "#EF4444": "Crimson Red",
    "#8B5CF6": "Violet Aura",
    "#EC4899": "Rose Pink",
    "#14B8A6": "Aqua Teal",
  };
  colorName.value = names[newColor] || "Custom Color";
});

watch(customRanges, () => {

  const parts = customRanges.value
    .filter(r => r.from && r.to)
    .map(r => `${r.from}-${r.to}`);

  form.value.hours = parts.join(",");

},{ deep:true });

watch(hoursType, (val)=>{
  if(val !== "Custom"){
    form.value.hours = val;
  }
});



watch(
  () => form.value.url,
  async (newVal) => {

    if (!newVal || newVal.length < 3) {
      return;
    }

    try {
      const res = await checkBookingPageUrl(newVal, form.value.id);

      // Assuming API returns { available: true/false }
      if (res.available==false) {
      //   toast1.success("URL is available");
      // } else {
        toast1.error("This page URL is not available");
        form.value.url = "";
        await nextTick();
      }

    } catch (e) {
      toast1.error("Error checking URL.");
    }
  }
);



// === File Upload Preview ===
// const fileInput = ref(null);
// const triggerUpload = () => fileInput.value.click();
// const handleFile = (e) => {
//   const file = e.target.files[0];
//   if (file) form.value.image = URL.createObjectURL(file);
// };

// === Payment Gateways ===
const gateways = [
  { name: "Stripe", desc: "Card & wallet payments", logo: "bx bxl-stripe", color: "#635BFF", status: "Not Connected" },
  { name: "PayPal", desc: "Trusted global gateway", logo: "bx bxl-paypal", color: "#00457C", status: "Not Connected" },
  // { name: "Razorpay", desc: "Fast Indian payments", logo: "bx bx-wallet", color: "#0C75FF", status: "Not Connected" },
];

const nextStep = async () => {
  // === Step-wise validation ===
  if (step.value === 0) {
    if (!form.value.title?.trim()) {
      toast1.error("Please enter a booking page title.");
      return;
    }
    if (!form.value.url?.trim()) {
      toast1.error("Please enter a custom booking page URL.");
      return;
    }
    // if (!form.value.intro?.trim()) {
    //   toast1.error("Please add an intro message for visitors.");
    //   return;
    // }
  }

  if (step.value === 1) {
    if (!form.value.color) {
      toast1.error("Please select a brand color.");
      return;
    }
    if (!form.value.layout) {
      toast1.error("Please choose a booking layout style.");
      return;
    }
    // if (form.value.package.length<=0) {
    //   toast1.error("Please select a package plan.");
    //   return;
    // }
  }

  if (step.value === 2) {
    if (!form.value.duration) {
      toast1.error("Please select a meeting duration.");
      return;
    }
    if (!form.value.hours) {
      toast1.error("Please select your available hours.");
      return;
    }
    if (form.value.days.length === 0) {
      toast1.error("Please select at least one available day.");
      return;
    }

    if (form.value.hours === "Custom") {

      if (!customRanges.value.length) {
        toast1.error("Add at least one time range");
        return;
      }

      for (let r of customRanges.value) {
        if (!r.from || !r.to) {
          toast1.error("Fill all custom time ranges");
          return;
        }
      }
    }

  }

  // if (step.value === 3) {
  //   const hasConnected = calendars.some((c) => c.status === "Connected");
  //   if (!hasConnected) {
  //     toast1.error("Please connect at least one calendar integration.");
  //     return;
  //   }
  // }

  // if (step.value === 4) {
  //   const hasGateway = gateways.some((g) => g.status === "Connected");
  //   if (!hasGateway) {
  //     toast1.error("Please connect at least one payment gateway.");
  //     return;
  //   }
  // }

  if (step.value === 5) {
    if (!selectedForm.value) {
      toast1.error("Please select a routing form layout.");
      return;
    }
  }

  if (step.value === 6) {
    if (!selectedReminders.value.length) {
      toast1.error("Please choose at least one reminder option.");
      return;
    }
  }

  if (step.value === 7) {
    if (!form.value.location) {
      toast1.error("Please select a meeting location type.");
      return;
    }
    if (form.value.location === "custom" && !form.value.customLink?.trim()) {
      toast1.error("Please enter your custom meeting link.");
      return;
    }
  }

  // === Final Submit ===
  if (step.value === steps.length - 1) {
    await submitBookingPage();
  } else {
    step.value++;
    scrollToTop();
    // toast1.success(`Step ${step.value + 1} loaded successfully`);
  }
};

const prevStep = () => {
  if (step.value > 0) step.value--;
  scrollToTop();
};
const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

// === UX Toast Helper ===
const toast = ref({ show: false, type: "", message: "" });
const showToast = (message, type = "success") => {
  toast.value = { show: true, type, message };
  setTimeout(() => (toast.value.show = false), 3000);
};

// === API Integration ===
const submitBookingPage = async () => {
  try {
    isSubmitting.value = true;

    const userData = JSON.parse(sessionStorage.getItem("ldata"));
    const userId = userData?.user_id || "";

    const payload = {
      booking_page_id: form.value.id,
      booking_page_title: form.value.title,
      booking_page_url: form.value.url,
      booking_page_intro: form.value.intro,
      booking_page_recurring: form.value.recurring?"1":"0",
      booking_page_days: form.value.days.join(","),
      booking_page_color: form.value.color,
      booking_page_img: form.value.image,
      booking_page_layout_type: form.value.layout,
      booking_page_duration: form.value.duration,
      booking_page_schedule: "default",
      booking_page_hours: form.value.hours,
      booking_page_from: form.value.customFrom,
      booking_page_to: form.value.customTo,
      booking_page_buffer_time: form.value.buffer,
      booking_page_google_calender: (calDt.value!=null && calDt.value.intigration_id!=undefined && calDt.value.intigration_id!=null)?calDt.value.intigration_id:"",
      booking_page_pay_gateway: gateways.map(g => `${g.name}:${g.status}`).join(", "),
      booking_page_packages: JSON.stringify(form.value.package),
      booking_page_form_layout: selectedForm.value,
      booking_page_reminders: selectedReminders.value.join(", "),
      booking_page_meet_mode: form.value.location,
      booking_page_location: form.value.customLink,
      booking_page_cre_by: userId,
      booking_page_status: "Active",
      business_timezone:Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    console.log("check",payload);
    const res = await createBookPage(payload);

    if (res.status) {
      showToast("🎉 Booking Page Created Successfully!", "success");
      setTimeout(() => router.push("/booking_pages"), 1500);
    } else {
      showToast(res.success || "Failed to create booking page", "danger");
    }
  } catch (err) {
    console.error("submitBookingPage error:", err);
    showToast("Something went wrong while creating the page", "danger");
  } finally {
    isSubmitting.value = false;
  }
};
const resetform = () =>{
  selectedForm.value = "";
}
const gotoformadd = ()=>{
  window.location.href = "/new_form?rtype=add_booking_page";
}
const new_package = ()=>{
  window.location.href = "/new_package";
}
const getMyPackages = async() =>{
  const userData = JSON.parse(sessionStorage.getItem("ldata"));
  const userId = userData?.user_id || "";

  const res = await getPackages(userId,"none","","");
  if (res?.status && res?.records?.length > 0) {
    packages.value = res.records || [];
  }
  else{
    packages.value = [];
  }
  const res1 = await getForms(userId,"none","","");
  if (res1?.status && res1?.records?.length > 0) {
    routingForms.value = res1.records || [];
  }
  else{
    routingForms.value = [];
  }
}

const fillFormWithPageData = (page) => {
  form.value.id = page.booking_page_id || "";
  form.value.title = page.booking_page_title || page.title || "";
  form.value.url = page.booking_page_url || "";
  form.value.intro = page.booking_page_intro || "";
  form.value.recurring = page.booking_page_recurring === "1" || page.recurring;
  form.value.color = page.booking_page_color || "#111827";
  form.value.layout = page.booking_page_layout_type || page.layout || "Month";
  form.value.duration = page.booking_page_duration || "";
  // form.value.hours = page.booking_page_hours || "";
  form.value.customFrom = page.booking_page_from || "";
  form.value.customTo = page.booking_page_to || "";
  form.value.buffer = page.booking_page_buffer_time || 0;
  form.value.location = page.booking_page_location || "";
  form.value.customLink = page.booking_page_meet_link || "";
const h = page.booking_page_hours || "";

if(h.includes("-") && h.includes(",")){
  hoursType.value = "Custom";

  customRanges.value = h.split(",").map(r=>{
    const p = r.split("-");
    return { from:p[0], to:p[1] };
  });

  form.value.hours = h;
}
else{
  hoursType.value = h;
  form.value.hours = h;
}


try {
    form.value.package = JSON.parse(page.booking_page_packages) || [];
  } catch {
    form.value.package = [];
  }

  if (page.booking_page_days) {
    form.value.days = page.booking_page_days.split(",");
  } else {
    form.value.days = ["Monday","Tuesday","Wednesday","Thursday","Friday"];
  }
};

onMounted(async () => {
  

  baseUrl.value = mainBaseUrl+"appointment/";
  if(sessionStorage.getItem("booking_page_data") && sessionStorage.getItem("booking_page_data").length>0){
    const savedPage = sessionStorage.getItem("booking_page_data");
    if (savedPage) {
      const page = JSON.parse(savedPage);
      fillFormWithPageData(page);
    }
  }
  else{
     try{
      const pid = route.query.pid;

        if (pid) {
          const txt1 = atob(pid);
          const page = JSON.parse(txt1);
          sessionStorage.setItem("booking_page_data",JSON.stringify(page));
          fillFormWithPageData(page);
        }

    }
    catch(e){
      console.log("sdfsad",e);
    }
  }
  getMyPackages();
  const userData = JSON.parse(sessionStorage.getItem("ldata"));
const userId = userData?.user_id || "";
  calDt.value = await checkIntegration(userId, "Google Calendar", "google_calendar");
  isCalendrConnected.value = calDt.value.status;
  if(isCalendrConnected.value){
    calendars[0].status = "Connected";
  }

  var paypal = await checkIntegration(userId, "PayPal", "payment");
  var stripe = await checkIntegration(userId, "Stripe", "payment");
  // var razorpay = await checkIntegration(userId, "Razorpay", "payment");
  
  gateways[0].status = (paypal.status && paypal.intigration.intigration_token1.length>0)? "Connected" : "Not Connected"
  gateways[1].status = (stripe.status && stripe.intigration.intigration_token1.length>0)? "Connected" : "Not Connected"
  // gateways[2].status = (razorpay.status && razorpay.intigration.intigration_token1.length>0)? "Connected" : "Not Connected"

  await nextTick();
});

</script>


<style scoped>

/* Circular Logo Upload */
.upload-circle {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-align: center;
}
.upload-circle:hover {
  background: #f3f4f6;
  border-color: #111827;
  transform: translateY(-2px);
}
.upload-circle i {
  font-size: 1.6rem;
  color: #111827;
  margin-bottom: 4px;
}
.upload-circle span {
  font-size: 0.8rem;
  color: #6b7280;
}
.upload-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

/* Brand Color Box */
.brand-color-box {
  display: flex;
  flex-direction: column;
  
  gap: 0.5rem;
}
.brand-color-box label {
  font-weight: 600;
  color: #374151;
}
.brand-color-box input[type="color"] {
  width: 60px;
  height: 60px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  background: none;
}
.color-code {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Layout Tabs */
.layout-tabs {
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}
.layout-tab {
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.layout-tab:hover {
  background: #f9fafb;
}
.layout-tab.active {
  background: #111827;
  color: #fff;
  border-color: #111827;
  transform: translateY(-2px);
}

/* Layout Preview Cards */
.layout-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 1.2rem;
}
.layout-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.layout-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.layout-card.active {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}
.layout-card .layout-icon {
  font-size: 2rem;
  color: #111827;
  margin-bottom: 0.6rem;
}
.layout-card.active .layout-icon {
  color: #111827;
  transform: scale(1.1);
}
.layout-card h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.3rem;
}
.layout-card p {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .branding-top {
    flex-direction: column;
    align-items: flex-start;
  }
  .layout-tabs {
    flex-wrap: wrap;
  }
  .layout-preview-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 480px) {
  .layout-preview-grid {
    grid-template-columns: 1fr;
  }
}

.add-booking-wrapper {
  background: #fff;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
  margin: 1rem auto;
  max-width: 1200px;
}

/* HEADER */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}
.header-left h2 {
  color: #111827;
  font-size: 1.7rem;
  font-weight: 700;
}
.header-left p {
  color: #6b7280;
  font-size: 0.95rem;
}
.btn-help {
  background: #111827;
  color: #fff;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
}

/* BODY */
.page-body {
  display: flex;
  gap: 2rem;
}
.step-nav {
  width: 30%;
  border-right: 1px solid #e5e7eb;
  padding-right: 1rem;
}
.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 1rem 0.5rem;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s ease;
}
.step-item.active,
.step-item:hover {
  background: #f3f4f6;
}
.step-item i {
  font-size: 1.4rem;
  color: #1e3a8a;
}
.step-info h4 {
  font-size: 1rem;
  margin: 0;
  color: #111827;
}
.step-info p {
  font-size: 0.8rem;
  color: #6b7280;
}
.step-body {
  width: 70%;
}

/* Availability Tabs */
.availability-tabs {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}
.tab-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
}
.tab-card h4 {
  margin-bottom: 0.6rem;
  color: #111827;
}
.option-grid {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.option-btn {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.25s ease;
}
.option-btn.active,
.option-btn:hover {
  background: #111827;
  color: #fff;
  border-color: #111827;
}
.custom-hours {
  margin-top: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.buffer-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.6rem;
}
/* === Custom Hours Section (Time Fields) === */
.custom-hours {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem 1.2rem;
  margin-top: 1rem;
  transition: all 0.3s ease;
}

.custom-hours label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}

.custom-hours input[type="time"] {
  flex: 1;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
  color: #111827;
  font-size: 0.9rem;
  transition: all 0.25s ease;
  font-family: "Inter", sans-serif;
}

.custom-hours input[type="time"]:focus {
  outline: none;
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
  background: #fff;
}

/* Smooth fade animation when this section appears */
.fade-smooth-enter-active,
.fade-smooth-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-smooth-enter-from,
.fade-smooth-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* === Mobile Responsiveness === */
@media (max-width: 640px) {
  .custom-hours {
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
    padding: 0.8rem;
  }

  .custom-hours label {
    font-size: 0.85rem;
  }

  .custom-hours input[type="time"] {
    width: 100%;
  }
}

/* Payments */
.payment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
}
.payment-card {
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: 0.3s;
}
.payment-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.gateway-left {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}
.gateway-logo {
  height: 45px;
  width: 45px;
  border-radius: 50%;
  background: #000;
  color: #fff;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.gateway-logo img {
  width: 28px;
}
.gateway-info h4 {
  font-size: 1rem;
  color: #111827;
  margin: 0;
}
.gateway-info p {
  font-size: 0.8rem;
  color: #6b7280;
}
.btn-black {
  background: #111827;
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
}
.status {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.4rem;
}

/* Review */
.review-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}
.review-section {
  flex: 1;
  min-width: 250px;
}
.review-section h4 {
  margin-bottom: 0.6rem;
  color: #111827;
}
.review-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.review-section li {
  margin-bottom: 0.4rem;
  color: #374151;
}

/* Footer Buttons */
.form-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
}
.btn-primary {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.6rem;
  cursor: pointer;
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.6rem;
  cursor: pointer;
}

/* Animations */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.fade-in {
  animation: fadeIn 0.6s ease;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 992px) {
  .page-body {
    flex-direction: column;
  }
  .step-nav,
  .step-body {
    width: 100%;
  }
}
@media (max-width: 992px) {
  .page-body {
    flex-direction: column;
  }

  .step-nav,
  .step-body {
    width: 100%;
  }

  .step-nav {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1rem;
    margin-bottom: 1.5rem;
  }

  .step-item {
    flex-direction: row;
    align-items: center;
    padding: 0.8rem;
  }

  .step-info h4 {
    font-size: 0.95rem;
  }

  .step-info p {
    display: none;
  }

  .availability-tabs {
    gap: 1rem;
  }

  .tab-card {
    padding: 0.8rem;
  }

  .payment-grid {
    grid-template-columns: 1fr;
  }

  .review-card {
    flex-direction: column;
  }

  .review-section {
    min-width: 100%;
  }

  .btn-primary,
  .btn-secondary {
    width: 48%;
    text-align: center;
  }
}

/* Small Devices */
@media (max-width: 576px) {
  .add-booking-wrapper {
    padding: 1.2rem;
  }

  .header-left h2 {
    font-size: 1.4rem;
  }

  .header-left p {
    font-size: 0.85rem;
  }

  .btn-help {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }

  .option-grid {
    flex-wrap: wrap;
  }

  .option-btn {
    flex: 1 1 45%;
    text-align: center;
  }

  .custom-hours {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
  }

  .payment-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .gateway-right {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .form-footer {
    flex-direction: column;
    gap: 0.8rem;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}

/* Smooth fade transition for dynamic elements */
.fade-smooth-enter-active,
.fade-smooth-leave-active {
  transition: opacity 0.3s ease;
}
.fade-smooth-enter-from,
.fade-smooth-leave-to {
  opacity: 0;
}
/* === STEP 1: Booking Page Basics === */
.step-body h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.4rem;
}

.step-desc {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 1.6rem;
}

/* Basics form layout */
.step-body .form-group {
  margin-bottom: 1.2rem;
}

.step-body .form-group label {
  font-weight: 600;
  color: #374151;
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.9rem;
}

.step-body input[type="text"],
.step-body input[type="email"],
.step-body input[type="number"],
.step-body select,
.step-body textarea {
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  font-size: 0.95rem;
  color: #111827;
  transition: all 0.25s ease;
}

.step-body input:focus,
.step-body select:focus,
.step-body textarea:focus {
  outline: none;
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* URL box */
.url-box {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  overflow: hidden;
  background: #f9fafb;
}
.url-box span {
  background: #e5e7eb;
  padding: 0.8rem 1rem;
  font-size: 0.9rem;
  color: #111827;
  font-weight: 500;
  border-right: 1px solid #d1d5db;
}
.url-box input {
  border: none;
  flex: 1;
  background: transparent;
  padding: 0.8rem 1rem;
}

/* Recurring Checkbox */
.checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 1rem;
  cursor: pointer;
  user-select: none;
}

.checkbox input {
  width: 18px;
  height: 18px;
  accent-color: #111827;
  cursor: pointer;
}

.checkbox span {
  color: #111827;
  font-weight: 500;
  font-size: 0.9rem;
}

/* Animation for smooth entry */
.fade-in {
  animation: fadeInStep 0.6s ease;
}

@keyframes fadeInStep {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Force scoped styles to apply correctly */
:deep(.form-group input),
:deep(.form-group textarea),
:deep(.form-group select) {
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  font-size: 0.95rem;
  font-family: "Inter", sans-serif;
  transition: all 0.25s ease;
  box-sizing: border-box;
}

:deep(.form-group input::placeholder),
:deep(.form-group textarea::placeholder) {
  color: #9ca3af;
  font-size: 0.9rem;
}

:deep(.form-group input:focus),
:deep(.form-group textarea:focus),
:deep(.form-group select:focus) {
  outline: none;
  border-color: #111827;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}


/* === 📱 Mobile View Fixes Only === */
@media (max-width: 768px) {
  /* Branding top layout fix */
  .branding-top {
    flex-direction: column !important;
    align-items: center !important;
    text-align: center;
    gap: 1.2rem;
  }

  .upload-circle {
    margin: 0 auto;
  }

  .brand-color-box {
    align-items: center !important;
  }

  /* Layout tabs alignment */
  .layout-tabs {
    justify-content: center !important;
    flex-wrap: wrap !important;
    gap: 0.8rem !important;
  }

  .layout-tab {
    flex: 1 1 45%;
    justify-content: center;
  }

  /* Preview grid responsive */
  .layout-preview-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 1rem !important;
  }

  /* Header spacing */
  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem;
  }

  /* Step navigation fix */
  .step-nav {
    border-right: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    padding-bottom: 1rem;
    margin-bottom: 1.2rem;
  }

  /* Form footer buttons full width */
  .form-footer {
    flex-direction: column !important;
    gap: 0.8rem !important;
  }
  .form-footer .btn-primary,
  .form-footer .btn-secondary {
    width: 100% !important;
    text-align: center !important;
  }
}

/* Small mobile optimization */
@media (max-width: 480px) {
  .layout-preview-grid {
    grid-template-columns: 1fr !important;
  }

  .layout-card {
    padding: 1rem !important;
  }

  .option-btn {
    flex: 1 1 48% !important;
    text-align: center !important;
  }

  .payment-card {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .btn-help {
    width: 100% !important;
    text-align: center !important;
  }

  .add-booking-wrapper {
    padding: 1.2rem !important;
  }
}
/* === 🎨 Pricing Branding Step Styling === */

/* --- General Animations --- */
.animate-fadein {
  animation: fadeIn 0.5s ease both;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* === Branding Top Layout === */
.branding-top {
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

/* --- Logo Upload --- */
.upload-circle {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  border: 2px dashed var(--border);
  background: var(--card);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}
.upload-circle:hover {
  transform: translateY(-2px);
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.1);
}
.upload-circle i {
  font-size: 1.6rem;
  color: var(--text);
}
.upload-circle span {
  font-size: 0.8rem;
  color: var(--muted);
}
.upload-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.branding-top {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 2.5rem;
}

.color-card {
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
}

.color-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(17, 24, 39, 0.08);
  border-color: #111827;
}

.color-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}
.color-preview {
  width: 54px;
  height: 54px;
  border-radius: 12px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.color-preview i {
  color: #fff;
  font-size: 1.3rem;
  position: absolute;
  opacity: 0.6;
}
.color-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.color-meta h5 {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text);
  margin: 0;
}
.color-meta span {
  font-size: 0.8rem;
  color: var(--muted);
}
.color-palette {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px var(--border);
  transition: all 0.25s ease;
}
.color-swatch:hover {
  transform: scale(1.15);
  box-shadow: 0 0 0 2px var(--primary);
}

/* === Plan Info Grid === */
.plan-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.2rem;
  margin-top: 1.2rem;
}
.plan-info-grid .form-group label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text);
}
.plan-info-grid .form-group input,
.plan-info-grid .form-group select {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  color: var(--text);
  background: #f9fafb;
  transition: all 0.3s ease;
}
.plan-info-grid .form-group input:focus,
.plan-info-grid .form-group select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === Layout Tabs === */
.layout-tabs {
  display: flex;
  gap: 0.8rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.layout-tab {
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--text);
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.layout-tab:hover {
  background: #f3f4f6;
}
.layout-tab.active {
  background: #111827;
  color: #fff;
  border-color: #111827;
  transform: translateY(-2px);
}

/* === Layout Previews === */
.layout-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.2rem;
  margin-top: 1rem;
}
.layout-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.layout-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
}
.layout-card.active {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}
.layout-header {
  border-radius: 8px;
  padding: 1rem;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
}
.layout-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.layout-header .price {
  font-weight: 700;
  font-size: 1rem;
}
.features {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
}
.features li {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text);
  font-size: 0.85rem;
  margin-bottom: 0.3rem;
}
.features i {
  color: var(--primary);
  font-size: 1rem;
}
.btn-select {
  background: #111827;
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}
.btn-select:hover {
  background: #1f2937;
  transform: translateY(-2px);
}

/* === Responsive === */
@media (max-width: 768px) {
  .branding-top {
    flex-direction: column;
    align-items: flex-start;
  }
  .color-card {
    width: 100%;
  }
  .layout-preview-grid {
    grid-template-columns: 1fr;
  }
}
.section-title {
  margin-top: 1.8rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0.6rem;
}

/* === Layout Cards Grid === */
.layout-preview-grid.booking-layouts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1.2rem;
  align-items: stretch;
}

/* === Booking Layout Cards === */
.layout-card.improved {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.35s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  overflow: hidden;
  padding: 1rem;
  position: relative;
}

.layout-card.improved:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
}

.layout-card.improved.active {
  border: 2px solid var(--primary);
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.15);
}

/* === Layout Visual Simulation === */
.layout-visual {
  border-radius: 10px;
  overflow: hidden;
  background: #f9fafb;
  border: 1px solid var(--border);
  transition: all 0.3s ease;
}

.layout-top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.6rem 0.8rem;
  color: #fff;
  font-weight: 600;
}

.layout-top i {
  font-size: 1.2rem;
}

.layout-body {
  padding: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.layout-row {
  height: 10px;
  background: #e5e7eb;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.layout-card.improved:hover .layout-row {
  background: #d1d5db;
}

/* === Details === */
.layout-details {
  margin-top: 0.8rem;
}

.layout-details h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 4px;
}

.layout-details p {
  font-size: 0.85rem;
  color: var(--muted);
  margin: 0;
}

/* === Footer === */
.layout-footer {
  margin-top: 1rem;
  text-align: right;
}

.btn-select {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-select:hover {
  background: #1f2937;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .layout-preview-grid.booking-layouts {
    grid-template-columns: 1fr;
  }
}
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text);
  margin-top: 1.5rem;
  margin-bottom: 0.4rem;
}

/* === Layout Grid (3 in a row) === */
.booking-layout-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 1.5rem;
}

/* === Card Base === */
.booking-layout-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.4rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}
.booking-layout-card:hover {
  transform: translateY(-4px);
  border-color: #111827;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}
.booking-layout-card.active {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === Illustration Wrapper === */
.layout-illustration {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1rem;
  height: 130px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

/* === Month View (Calendar Dots) === */
.month-view {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.calendar-dot {
  width: 14px;
  height: 14px;
  background: #d1d5db;
  border-radius: 50%;
  transition: all 0.3s ease;
}
.booking-layout-card.active .calendar-dot {
  background: #111827;
}

/* === Week View (Stacked Lines) === */
.week-view {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 80%;
}
.week-line {
  height: 8px;
  background: #d1d5db;
  border-radius: 4px;
  transition: background 0.3s ease;
}
.booking-layout-card.active .week-line {
  background: #111827;
}

/* === Day View (Blocks) === */
.day-view {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 60%;
}
.day-block {
  height: 10px;
  background: #d1d5db;
  border-radius: 6px;
}
.day-block.long {
  width: 100%;
}
.day-block.short {
  width: 70%;
}
.booking-layout-card.active .day-block {
  background: #111827;
}

/* === Footer === */
.layout-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.layout-footer input[type="radio"] {
  accent-color: #111827;
  cursor: pointer;
}
.layout-footer label {
  font-size: 0.9rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
}

/* === Responsive === */
@media (max-width: 992px) {
  .booking-layout-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .booking-layout-grid {
    grid-template-columns: 1fr;
  }
}

/* === Calendar Integration Cards === */
.calendar-grid {
  margin-top: 1rem;
}

.calendar-card {
  background: var(--card, #fff);
  margin-bottom: 1.5rem;
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 14px;
  padding: 1.2rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  min-height: 120px;
}

.calendar-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

/* --- Left Section --- */
.calendar-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.calendar-logo {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  background-color: #000;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.calendar-card:hover .calendar-logo {
  transform: rotate(-5deg) scale(1.05);
}

.calendar-logo i {
  color: #fff;
  font-size: 1.8rem;
}

.calendar-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text, #111827);
  margin-bottom: 4px;
}

.calendar-info p {
  font-size: 0.85rem;
  color: var(--muted, #6b7280);
  margin: 0;
}

/* --- Right Section --- */
.calendar-right {
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.btn-connect {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-connect:hover {
  background: #1f2937;
  transform: translateY(-2px);
}

.btn-connect.connected {
  background: #10b981;
  cursor: default;
}

.btn-connect.upcoming {
  background: #9ca3af;
  color: #f3f4f6;
  cursor: not-allowed;
}

/* --- Status Colors --- */
.status {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status.connected {
  color: #10b981;
}

.status.notConnected {
  color: #ef4444;
}

.status.upcoming {
  color: #9ca3af;
}

/* --- Responsive --- */
@media (max-width: 768px) {
  .calendar-card {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    gap: 1rem;
    padding: 1rem;
  }

  .calendar-right {
    align-items: flex-start;
  }

  .calendar-logo {
    width: 46px;
    height: 46px;
  }
}
/* === Routing Forms === */
.form-layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.form-layout-header h4 {
  font-size: 1rem;
  color: #111827;
  font-weight: 600;
}

.btn-add {
  background: #059669;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.45rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}
.btn-add:hover {
  background: #047857;
  transform: translateY(-2px);
}

/* === Form Grid === */
.routing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.2rem;
  margin-top: 1rem;
}

/* === Form Card === */
.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 200px;
}
.form-card:hover {
  transform: translateY(-4px);
  border-color: #111827;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
}
.form-card.active {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === Preview Illustration === */
.form-preview {
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 0.8rem;
  margin-bottom: 0.8rem;
}
.form-top {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
}
.circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #d1d5db;
}
.lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.line {
  height: 4px;
  border-radius: 4px;
  background: #d1d5db;
}
.line.short {
  width: 40px;
}
.line.long {
  width: 70px;
}

/* === Dot Grid (Simulating Form Fields) === */
.dot-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e5e7eb;
}
.form-card.active .dot {
  background: #111827;
}

/* === Footer === */
.form-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #374151;
  font-weight: 500;
}
.form-footer input[type="radio"] {
  accent-color: #111827;
  cursor: pointer;
}

/* === Responsive === */
@media (max-width: 768px) {
  .routing-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 500px) {
  .routing-grid {
    grid-template-columns: 1fr;
  }
}
/* === Reminders Section === */
.reminder-options {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  padding-left: 0.3rem;
}

.reminder-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  color: #111827;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.reminder-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  appearance: none;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reminder-checkbox input[type="checkbox"]:checked {
  background-color: #065f46;
  border-color: #065f46;
  box-shadow: 0 0 0 3px rgba(6, 95, 70, 0.2);
}

.reminder-checkbox input[type="checkbox"]:checked::after {
  content: "✔";
  font-size: 0.8rem;
  color: #fff;
}

.reminder-checkbox:hover span {
  color: #065f46;
}

/* === Custom Reminder Button === */
.btn-custom-reminder {
  background: #065f46;
  color: #fff;
  border: none;
  width: 60%;
  margin: 1.2rem auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-custom-reminder:hover {
  background: #047857;
  transform: translateY(-2px);
}

/* === Responsive === */
@media (max-width: 768px) {
  .btn-custom-reminder {
    width: 100%;
  }
}

/* === Meeting Location Grid === */
.location-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.2rem;
  margin-top: 1.5rem;
}

/* === Location Card === */
.location-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.2rem 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
  position: relative;
}

.location-card:hover {
  transform: translateY(-4px);
  border-color: #111827;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.location-card.active {
  border: 2px solid #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === Icon Box === */
.location-icon {
  height: 45px;
  width: 45px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  font-size: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* === Details === */
.location-details {
  flex: 1;
  padding-left: 1rem;
}

.location-details h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.location-details p {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 4px 0 0 0;
}

/* === Radio Selection === */
.location-select input[type="radio"] {
  accent-color: #111827;
  transform: scale(1.2);
  cursor: pointer;
}

/* === Custom Link Input === */
.custom-link-box {
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.custom-link-box label {
  font-weight: 600;
  color: #374151;
}

.custom-link-box input {
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0.8rem;
  font-size: 0.95rem;
  color: #111827;
  transition: all 0.25s ease;
}

.custom-link-box input:focus {
  outline: none;
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === Responsive === */
@media (max-width: 768px) {
  .location-grid {
    grid-template-columns: 1fr;
  }
}
/* ==========================================================
   📱 FINAL RESPONSIVE OPTIMIZATION — Add Booking Page
   ========================================================== */

/* === Tablet View (max-width: 992px) === */
@media (max-width: 992px) {
  /* Step Navigation & Body Layout */
  .page-body {
    flex-direction: column !important;
    gap: 1.5rem !important;
  }

  .step-nav {
    width: 100% !important;
    border-right: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    padding-bottom: 1rem !important;
  }

  .step-body {
    width: 100% !important;
  }

  /* Reduce padding for main wrapper */
  .add-booking-wrapper {
    padding: 1.5rem !important;
  }

  /* Fix tab cards + option grids */
  .tab-card {
    padding: 0.8rem !important;
  }

  /* Payment cards single column */
  .payment-grid {
    grid-template-columns: 1fr !important;
  }

  /* Routing form cards grid */
  .routing-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  /* Booking layout cards */
  .booking-layout-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  /* Reduce step-item density */
  .step-item {
    padding: 0.8rem !important;
  }

  /* Reduce gap in plan info */
  .plan-info-grid {
    gap: 1rem !important;
  }
}

/* === Mobile View (max-width: 768px) === */
@media (max-width: 768px) {
  /* Header */
  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .header-left h2 {
    font-size: 1.4rem !important;
  }
  .header-left p {
    font-size: 0.9rem !important;
  }

  .btn-help {
    width: 100% !important;
    text-align: center !important;
  }

  /* Step nav compact mode */
  .step-item {
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-start !important;
  }
  .step-info p {
    display: none !important;
  }

  /* Branding section column layout */
  .branding-top {
    flex-direction: column !important;
    align-items: center !important;
    text-align: center !important;
    gap: 1.2rem !important;
  }
  .upload-circle {
    margin: 0 auto !important;
  }

  /* Booking layout cards single column */
  .booking-layout-grid {
    grid-template-columns: 1fr !important;
  }

  /* Routing form grid single column */
  .routing-grid {
    grid-template-columns: 1fr !important;
  }

  /* Calendar cards stack */
  .calendar-card {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  /* Location cards full width */
  .location-grid {
    grid-template-columns: 1fr !important;
  }

  /* Footer buttons full width */
  .form-footer {
    flex-direction: column !important;
    gap: 0.8rem !important;
  }
  .form-footer .btn-primary,
  .form-footer .btn-secondary {
    width: 100% !important;
  }

  /* Adjust padding */
  .add-booking-wrapper {
    padding: 1.2rem !important;
  }
}

/* === Small Mobile View (max-width: 480px) === */
@media (max-width: 480px) {
  /* Reduce form padding + input spacing */
  .step-body input,
  .step-body textarea,
  .step-body select {
    padding: 0.6rem 0.8rem !important;
    font-size: 0.85rem !important;
  }

  /* Step title size */
  .step-body h3 {
    font-size: 1.1rem !important;
  }

  /* Branding color section compact */
  .color-card {
    width: 100% !important;
  }

  /* Booking layout cards smaller padding */
  .booking-layout-card {
    padding: 1rem 0.8rem !important;
  }

  /* Routing forms + location cards tighter gap */
  .routing-grid,
  .location-grid {
    gap: 1rem !important;
  }

  /* Payment card stack fix */
  .payment-card {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  /* Buffer time input full width */
  .buffer-input {
    width: 100% !important;
  }

  /* Footer buttons full width */
  .form-footer button {
    width: 100% !important;
    padding: 0.8rem !important;
  }

  /* Bottom spacing */
  .add-booking-wrapper {
    margin-bottom: 2rem !important;
  }
}
/* ==========================================================
   📱 STEP NAVIGATION HORIZONTAL ON MOBILE
   ========================================================== */
@media (max-width: 768px) {
  .step-nav {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    border-bottom: 1px solid #e5e7eb !important;
    border-right: none !important;
    padding: 0.6rem 0 !important;
    margin-bottom: 1rem !important;
    gap: 0.6rem !important;
    scrollbar-width: none; /* Hide scrollbar Firefox */
  }

  .step-nav::-webkit-scrollbar {
    display: none; /* Hide scrollbar Chrome/Safari */
  }

  .step-item {
    flex: 0 0 auto !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    background: #f9fafb !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 10px !important;
    padding: 0.6rem 0.9rem !important;
    transition: all 0.3s ease;
  }

  .step-item.active {
    background: #111827 !important;
    color: #fff !important;
    border-color: #111827 !important;
  }

  .step-item.active i {
    color: #fff !important;
  }

  .step-item i {
    font-size: 1.2rem !important;
    margin-right: 6px !important;
    color: #1e3a8a !important;
  }

  .step-info h4 {
    font-size: 0.85rem !important;
    white-space: nowrap !important;
  }

  .step-info p {
    display: none !important;
  }
}

/* === Small Mobile (480px and below) === */
@media (max-width: 480px) {
  .step-item {
    padding: 0.5rem 0.8rem !important;
  }

  .step-info h4 {
    font-size: 0.8rem !important;
  }

  .step-item i {
    font-size: 1rem !important;
  }
}

/* === 📦 Packages Grid (3 in a Row) === */
.packages-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr); /* ✅ 3 items per row */
  gap: 1.5rem;
  margin-top: 1.5rem;
}

/* === Package Card === */
.package-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.4rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.package-card:hover {
  transform: translateY(-4px);
  border-color: #111827;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.package-card.active {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === Header: Title + Price === */
.package-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
}

.package-header h4 {
  font-size: 1.1rem;
  color: #111827;
  font-weight: 600;
  margin: 0;
}

.package-header .price {
  background: #111827;
  color: #fff;
  padding: 0.35rem 0.7rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* === Body: Duration Info === */
.package-body {
  margin: 0.8rem 0;
  color: #374151;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.package-body i {
  color: #1e3a8a;
  font-size: 1rem;
}

/* === Footer: Radio Button === */
.package-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 0.6rem;
}

.package-footer input[type="radio"] {
  accent-color: #111827;
  cursor: pointer;
  transform: scale(1.1);
}

.package-footer label {
  font-size: 0.85rem;
  color: #374151;
  font-weight: 500;
}

/* === Active & Hover Effects === */
.package-card:hover .price {
  background: #1f2937;
}

.package-card.active .price {
  background: #1f2937;
}

.package-card.active h4 {
  color: #1f2937;
}

/* === 💻 Responsive Adjustments === */
@media (max-width: 1024px) {
  .packages-grid {
    grid-template-columns: repeat(2, 1fr); /* 2 per row on tablets */
  }
}

@media (max-width: 640px) {
  .packages-grid {
    grid-template-columns: 1fr; /* 1 per row on mobile */
  }

  .package-header {
    flex-direction: column;
    gap: 6px;
  }

  .package-card {
    padding: 1rem;
  }
}

/* === Toast Notifications === */
.toast {
  position: fixed;
  bottom: 1.6rem;
  right: 1.6rem;
  padding: 0.9rem 1.4rem;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  font-weight: 600;
  font-size: 0.95rem;
  color: #fff;
  z-index: 99999;
  opacity: 0;
  animation: slideIn 0.4s ease forwards;
}
.toast.success {
  background: #16a34a; /* green */
}
.toast.error {
  background: #dc2626; /* red */
}
.toast.warning {
  background: #f59e0b; /* amber */
}
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Button Loading & Disabled States === */
.btn-primary[disabled],
.btn-secondary[disabled] {
  opacity: 0.6;
  pointer-events: none;
  cursor: not-allowed;
}

.btn-primary.loading::after {
  content: "…";
  margin-left: 6px;
  animation: dots 1.5s steps(3, end) infinite;
}
@keyframes dots {
  0%, 20% {
    content: "";
  }
  40% {
    content: ".";
  }
  60% {
    content: "..";
  }
  80%, 100% {
    content: "...";
  }
}

/* === Minor polish for color palette hover === */
.color-swatch {
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.color-swatch:hover {
  transform: scale(1.15);
  box-shadow: 0 0 0 2px var(--primary, #111827);
}

/* === Smooth fade transition === */
.fade-smooth-enter-active,
.fade-smooth-leave-active {
  transition: all 0.35s ease;
}
.fade-smooth-enter-from,
.fade-smooth-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* === Responsive fix for toast on small screens === */
@media (max-width: 640px) {
  .toast {
    bottom: 1rem;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    text-align: center;
    width: calc(100% - 2rem);
  }
}
/* Available Days */
.days-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 0.5rem;
}

.day-btn {
  padding: 0.6rem 1rem;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 500;
  transition: 0.25s;
}

.day-btn.active,
.day-btn:hover {
  background: #111827;
  color: #fff;
  border-color: #111827;
}

/* Mobile */
@media (max-width: 640px) {
  .day-btn {
    flex: 1 1 45%;
    text-align: center;
  }
}
.custom-multi-hours{
  margin-top:12px;
  display:flex;
  flex-direction:column;
  gap:10px;
}

.range-row{
  display:flex;
  gap:10px;
  align-items:center;
}

.range-row input{
  padding:8px;
  border:1px solid #d1d5db;
  border-radius:8px;
}

.add-btn{
  width:max-content;
  padding:6px 12px;
  background:#111827;
  color:#fff;
  border:none;
  border-radius:8px;
}

.remove-btn{
  background:#ef4444;
  color:#fff;
  border:none;
  padding:6px 8px;
  border-radius:6px;
}

</style>

